import React from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { useLang } from "@/i18n";

export const STATUS_META = {
  occupied: { color: "#10B981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.4)", icon: "user-round" },
  vacant: { color: "#64748B", bg: "rgba(100,116,139,0.12)", border: "rgba(100,116,139,0.35)", icon: "door-closed" },
  maintenance: { color: "#F59E0B", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)", icon: "wrench" },
  alert: { color: "#F43F5E", bg: "rgba(244,63,94,0.12)", border: "rgba(244,63,94,0.45)", icon: "triangle-alert" },
};

export const PRIORITY_META = {
  high: { color: "#F43F5E", label: "high" },
  medium: { color: "#F59E0B", label: "medium" },
  low: { color: "#64748B", label: "low" },
};

export function Lu({ name, ...props }) {
  const key = (name || "circle").split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("");
  const Cmp = Icons[key] || Icons.Circle;
  return <Cmp {...props} />;
}

export function StatCard({ icon, label, value, sub, accent = "#1E5EFF", delay = 0, testid }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="md-card p-5 relative overflow-hidden group"
    >
      <div className="absolute top-0 ltr:left-0 rtl:right-0 h-full w-1" style={{ background: accent }} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="mt-2 text-3xl font-extrabold font-mono" style={{ color: accent }}>{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground/70">{sub}</p>}
        </div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
             style={{ background: `${accent}1a`, border: `1px solid ${accent}33` }}>
          <Lu name={icon} className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </motion.div>
  );
}

export function SectionHeader({ title, subtitle, icon, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <div className="flex items-center gap-2.5">
          {icon && (
            <span className="w-9 h-9 rounded-lg md-gold-gradient flex items-center justify-center">
              <Lu name={icon} className="w-4.5 h-4.5 text-primary-foreground" style={{ width: 18, height: 18 }} />
            </span>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">{title}</h1>
        </div>
        {subtitle && <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function StatusPill({ status }) {
  const { t } = useLang();
  const m = STATUS_META[status] || STATUS_META.vacant;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
          style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
      {t(status)}
    </span>
  );
}

export function Loader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  );
}

export const CHART_COLORS = { gold: "#1E5EFF", teal: "#00C2B8", emerald: "#10B981", blue: "#3B82F6", amber: "#F59E0B", rose: "#F43F5E", slate: "#64748B" };
export const fmtSAR = (n) => new Intl.NumberFormat("en-US").format(Math.round(n));
